import React, { useState, useEffect } from 'react';
import { sitesV2Api, SiteMember } from '../../../api/sitesV2Api';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { RoleBadges } from '../../../shared/ui/RoleBadges';
import config from '../../../config/config';
import './UserSelector.css';

interface UserSelectorProps {
  siteId: number;
  typeId?: number; // 1=Question, 2=Note, 3=Supply
  selectedUserIds: number[];
  onSelectionChange: (userIds: number[]) => void;
  addableRoleIds: number[] | null;
}

const UserSelector: React.FC<UserSelectorProps> = ({
  siteId,
  typeId = 1, // По умолчанию Question
  selectedUserIds,
  onSelectionChange,
  addableRoleIds
}) => {
  const { user } = useAuth();
  const [members, setMembers] = useState<SiteMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSiteMembers();
  }, [siteId]);

  const loadSiteMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const membersData = await sitesV2Api.getSiteMembers(siteId);
      setMembers(membersData);
    } catch (err) {
      console.error('Error loading site members:', err);
      setError('Ошибка загрузки участников объекта');
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId: number) => {
    if (selectedUserIds.includes(userId)) {
      onSelectionChange(selectedUserIds.filter(id => id !== userId));
    } else {
      onSelectionChange([...selectedUserIds, userId]);
    }
  };

  const handleSelectAll = () => {
    // Выбираем всех, кого можно выбрать
    const allUserIds = members
      .filter(member => {
        const isCreator = member.userId === user?.id;
        if (isCreator) return false;
        if (addableRoleIds === null) return true; // Если права еще не загружены, разрешаем
        
        // Проверяем новые роли если есть, иначе fallback на старую роль
        if (member.roles && member.roles.length > 0) {
          return member.roles.some(r => r.isActive && addableRoleIds.includes(r.roleId));
        }
        return addableRoleIds.includes(member.roleId ?? -1);
      })
      .map(member => member.userId);
    onSelectionChange(allUserIds);
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (userId: number): string => {
    const colors = [
      '#ef4444', '#f97316', '#f59e0b', '#eab308',
      '#84cc16', '#22c55e', '#10b981', '#06b6d4',
      '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
      '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
    ];
    const index = userId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  const getParticipantsTitle = () => {
    const baseTitle = (() => {
      switch (typeId) {
        case 1:
          return 'Добавить участника';
        case 2:
          return 'Добавить наблюдателя';
        case 3:
          return 'Добавить участника';
        default:
          return 'Добавить участника';
      }
    })();

    if (selectedUserIds.length > 0) {
      return `${baseTitle} (${selectedUserIds.length})`;
    }
    return baseTitle;
  };

  if (loading) {
    return (
      <div className="user-selector">
        <div className="user-selector-title">
          <span>{getParticipantsTitle().replace(/\(\d+\)/, '')}</span>
          {selectedUserIds.length > 0 && (
            <span className="count-badge">{selectedUserIds.length}</span>
          )}
        </div>
        <div className="user-selector-loading">
          <i className="ri-loader-4-line ri-spin"></i>
          <span>Загрузка участников...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-selector">
        <div className="user-selector-title">
          <span>{getParticipantsTitle().replace(/\(\d+\)/, '')}</span>
          {selectedUserIds.length > 0 && (
            <span className="count-badge">{selectedUserIds.length}</span>
          )}
        </div>
        <div className="user-selector-error">
          <i className="ri-error-warning-line"></i>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="user-selector">
      <div className="user-selector-title">
        <span>{getParticipantsTitle().replace(/\(\d+\)/, '')}</span>
        {selectedUserIds.length > 0 && (
          <span className="count-badge">{selectedUserIds.length}</span>
        )}
      </div>

      {/* Кнопки управления */}
      <div className="user-selector-controls">
        <button 
          className="control-btn select-all"
          onClick={handleSelectAll}
          disabled={selectedUserIds.length === members.length}
        >
          <i className="ri-checkbox-multiple-line"></i>
          Все
        </button>
        <button 
          className="control-btn deselect-all"
          onClick={handleDeselectAll}
          disabled={selectedUserIds.length === 0}
        >
          <i className="ri-checkbox-blank-line"></i>
          Снять
        </button>
      </div>

      {/* Информационное сообщение убрано - создатель виден в списке */}

      {/* Список участников */}
      <div className="user-selector-list">
        {members.map(member => {
          const isSelected = selectedUserIds.includes(member.userId);
          const isCreator = member.userId === user?.id;
          
          // Проверяем, можно ли добавлять этого участника
          const canBeAdded = addableRoleIds === null 
            ? true // Если массив прав еще не загружен, считаем, что можно
            : (member.roles && member.roles.length > 0)
              ? member.roles.some(r => r.isActive && addableRoleIds.includes(r.roleId))
              : addableRoleIds.includes(member.roleId ?? -1);
            
          const isDisabled = isCreator || !canBeAdded;

          return (
            <div 
              key={member.userId}
              className={`user-selector-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => !isDisabled && handleUserToggle(member.userId)}
            >
              <div className="user-avatar">
                {(() => {
                  const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
                  const avatarUrl = member.avatarUrl 
                    ? (member.avatarUrl.startsWith('http') ? member.avatarUrl : `${apiBaseUrl}${member.avatarUrl}`)
                    : null;
                  
                  return avatarUrl ? (
                    <>
                      <img 
                        src={avatarUrl}
                        alt={member.userName || 'Аватар'}
                        className="avatar-image"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) {
                            fallback.style.display = 'flex';
                          }
                        }}
                      />
                      <div 
                        className="avatar-circle"
                        style={{ 
                          background: getAvatarColor(member.userId),
                          display: 'none'
                        }}
                      >
                        {getUserInitials(member.userName || 'U')}
                      </div>
                    </>
                  ) : (
                    <div 
                      className="avatar-circle"
                      style={{ background: getAvatarColor(member.userId) }}
                    >
                      {getUserInitials(member.userName || 'U')}
                    </div>
                  );
                })()}
                {isSelected && !isCreator && (
                  <div className="avatar-check">
                    <i className="ri-check-line"></i>
                  </div>
                )}
              </div>
              
              <div className="user-info">
                <div className="user-name">
                  {member.userName}
                  {isCreator && (
                    <span className="creator-badge">Создатель</span>
                  )}
                  {!canBeAdded && !isCreator && (
                    <span className="disabled-badge">у вас нет прав на добавление этого участника</span>
                  )}
                </div>
                <RoleBadges 
                  roles={member.roles} 
                  fallbackRoleName={member.roleName || 'Участник'}
                />
              </div>

              {/* Чекбокс убран - используется только галочка в аватаре */}
            </div>
          );
        })}
      </div>

      {/* Предупреждение о приватности */}
      {selectedUserIds.length === 0 && (
        <div className="user-selector-warning">
          <i className="ri-information-line"></i>
          <span>
            {typeId === 2 ? 'Заметка будет приватной (видна только вам)' : 
             typeId === 3 ? 'Заявка будет приватной (видна только вам)' :
             'Дискуссия будет приватной (видна только вам)'}
          </span>
        </div>
      )}
    </div>
  );
};

export default UserSelector;
